import { httpClient } from "./client";
import type {
  CheckinPassResponse,
  KioskCheckinFeed,
  KioskDevice,
  KioskDeviceCreate,
  KioskDeviceCreated,
  KioskExchangeRequest,
  KioskScanRequest,
  KioskScanResult,
  KioskSessionStart,
  KioskSessionStatus,
  KioskSettings,
  KioskUndoResult,
  RedemptionCreate,
  RedemptionPass,
  RedemptionStatusResponse,
} from "../types/generated";

/**
 * "Redeem one" (#2691) for the signed-in player.
 *
 * Creating a redemption opens a 10-minute intent for one visit of a pass (or an
 * unlimited membership) and returns its first 40 s check-in pass. Nothing is
 * used until a kiosk scans it; an expired or cancelled intent uses nothing.
 * Creating again for the same pass returns the same open intent.
 *
 * Errors the caller maps: 409 `not_redeemable` (expired, empty or not valid for
 * drop-in right now), 409 `payment_past_due` (the pass's billing failed: update
 * the card in Memberships), 409 `redemption_in_progress` (a scan is running),
 * 404 `redemption_not_found` (the intent ended: scanned, cancelled or expired),
 * and the guardian-control 403s.
 *
 * `status` says what became of a code: `open`, `consumed` (with the drop-in
 * session it admitted), `released` (a scan refused it, or it was cancelled) or
 * `expired`. Nothing is used unless it is `consumed`.
 */
export const checkinRedemptionApi = {
  create: (request: RedemptionCreate) =>
    httpClient.post<RedemptionPass>("/checkin/redemptions", request),

  status: (redemptionId: string) =>
    httpClient.get<RedemptionStatusResponse>(
      `/checkin/redemptions/${encodeURIComponent(redemptionId)}`,
    ),

  refreshPass: (redemptionId: string) =>
    httpClient.get<CheckinPassResponse>(
      `/checkin/redemptions/${encodeURIComponent(redemptionId)}/pass`,
    ),

  cancel: (redemptionId: string) =>
    httpClient.delete<void>(
      `/checkin/redemptions/${encodeURIComponent(redemptionId)}`,
    ),
};

/**
 * The entrance kiosk's own transport (#2691). There is no user session: the
 * exchange turns a one-time setup code into an HttpOnly kiosk cookie held by
 * the BFF, and the session/scan calls carry only that device session. A 401
 * means the device was revoked or expired and must be paired again; the http
 * client never redirects these paths to the player sign-in.
 */
export const kioskDeviceApi = {
  exchange: (request: KioskExchangeRequest) =>
    httpClient.post<KioskSessionStart>("/kiosk/exchange", request),

  getSession: () => httpClient.get<KioskSessionStatus>("/kiosk/session"),

  scan: (request: KioskScanRequest) =>
    httpClient.post<KioskScanResult>("/kiosk/scans", request),
};

const facilityPath = (facilityId: string) =>
  `/facilities/${encodeURIComponent(facilityId)}`;

/**
 * Front desk side of the scan kiosk (#2691), for signed-in staff.
 *
 * Devices, the live feed, resolve and undo need `facility:checkin` (undoing a
 * drop-in "Redeem one" also needs `facility:queue`); changing the kiosk mode
 * needs `facility:manage`. Creating a device or re-pairing returns a one-time
 * setup code (15 minutes) that is never shown again.
 *
 * The feed is newest first. Poll with the returned `cursor` as `since` for new
 * scans; `needs_a_person` and `devices` are always complete. Undo is idempotent
 * and has no time limit; `visit_restored` is true only when the admission used
 * a pass visit and it came back.
 */
export const kioskStaffApi = {
  getSettings: (facilityId: string) =>
    httpClient.get<KioskSettings>(`${facilityPath(facilityId)}/kiosk-settings`),

  updateSettings: (facilityId: string, settings: KioskSettings) =>
    httpClient.put<KioskSettings>(
      `${facilityPath(facilityId)}/kiosk-settings`,
      settings,
    ),

  listDevices: (facilityId: string) =>
    httpClient.get<KioskDevice[]>(`${facilityPath(facilityId)}/kiosk-devices`),

  createDevice: (facilityId: string, request: KioskDeviceCreate) =>
    httpClient.post<KioskDeviceCreated>(
      `${facilityPath(facilityId)}/kiosk-devices`,
      request,
    ),

  newSetupCode: (facilityId: string, deviceId: string) =>
    httpClient.post<KioskDeviceCreated>(
      `${facilityPath(facilityId)}/kiosk-devices/${encodeURIComponent(deviceId)}/setup-code`,
    ),

  revokeDevice: (facilityId: string, deviceId: string) =>
    httpClient.post<void>(
      `${facilityPath(facilityId)}/kiosk-devices/${encodeURIComponent(deviceId)}/revoke`,
    ),

  checkins: (
    facilityId: string,
    options: { since?: string; limit?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (options.since) params.set("since", options.since);
    if (options.limit) params.set("limit", String(options.limit));
    const query = params.toString();
    return httpClient.get<KioskCheckinFeed>(
      `${facilityPath(facilityId)}/kiosk/check-ins${query ? `?${query}` : ""}`,
    );
  },

  resolveCheckin: (facilityId: string, scanId: string) =>
    httpClient.post<void>(
      `${facilityPath(facilityId)}/kiosk/check-ins/${encodeURIComponent(scanId)}/resolve`,
    ),

  undoCheckin: (facilityId: string, scanId: string) =>
    httpClient.post<KioskUndoResult>(
      `${facilityPath(facilityId)}/kiosk/check-ins/${encodeURIComponent(scanId)}/undo`,
    ),
};
